import React, { useEffect, useState } from 'react';
import {
  TextField,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CategoryMaster } from '../../../../services/apiTypes';
import { defaultCategory } from '../../hooks/useCategory';
import useErrorModal from '../../../../hooks/useErrorModal';
import { API_CODE } from '../../../../_constants/apiConst';
import { MaxLength } from '../../../../_constants/maxLengthConst';
import { putCategoryMaster } from '../../../../services/api/categoryMaster';

type EditCategoryModalProps = {
  category: CategoryMaster;
  isOpen: boolean;
  isAdmin: boolean;
  handleSave: () => void;
  handleCancel: () => void;
};

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({
  category,
  isOpen,
  isAdmin,
  handleSave,
  handleCancel,
}) => {
  const { t } = useTranslation();
  const [editCategory, setEditCategory] =
    useState<CategoryMaster>(defaultCategory);
  const { getApiErrorMessage } = useErrorModal();
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isDisabledSaveBtn, setIsDisabledSaveBtn] = useState(false);

  useEffect(() => {
    if (category) {
      setEditCategory(category);
    }
  }, [category]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof CategoryMaster;
    setEditCategory({ ...editCategory, [fieldName]: value });
  };

  /** 保存ボタン押下 */
  const onClickSave = async () => {
    if (!editCategory.categoryId) {
      return;
    }
    setIsDisabledSaveBtn(true);
    setErrorMsg('');
    try {
      await putCategoryMaster(editCategory);
      handleSave();
    } catch (error) {
      setErrorMsg(getApiErrorMessage(error, API_CODE.CATEGORY_MASTER));
    } finally {
      setIsDisabledSaveBtn(false);
    }
  };

  /** ダイアログを閉じるときに実行する処理 */
  const closeModal = (isClose: boolean) => {
    if (isClose === true) {
      setErrorMsg('');
      setEditCategory(defaultCategory);
      handleCancel();
    }
  };

  return (
    <Dialog open={isOpen} onClose={closeModal}>
      <Box display="flex">
        <DialogTitle>{t('LBL_EDIT')}</DialogTitle>
        <Typography alignSelf="center" color={'error'}>
          {errorMsg}
        </Typography>
      </Box>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('LBL_CATEGORY_NAME_LANG', { 0: t('LBL_LANG_EN') })}
          type="text"
          fullWidth
          name="categoryName"
          value={editCategory.categoryName}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.CATEGORY_NAME,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_CATEGORY_NAME_LANG', { 0: t('LBL_LANG_JA') })}
          type="text"
          fullWidth
          name="categoryNameJp"
          value={editCategory.categoryNameJp}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.CATEGORY_NAME,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_CATEGORY_NAME_LANG', { 0: t('lang3') })}
          type="text"
          fullWidth
          name="categoryName03"
          value={editCategory.categoryName03}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.CATEGORY_NAME,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_CATEGORY_NAME_LANG', { 0: t('lang4') })}
          type="text"
          fullWidth
          name="categoryName04"
          value={editCategory.categoryName04}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.CATEGORY_NAME,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_CATEGORY_NAME_LANG', { 0: t('lang5') })}
          type="text"
          fullWidth
          name="categoryName05"
          value={editCategory.categoryName05}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.CATEGORY_NAME,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_CATEGORY_NAME_LANG', { 0: t('lang6') })}
          type="text"
          fullWidth
          name="categoryName06"
          value={editCategory.categoryName06}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.CATEGORY_NAME,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_CATEGORY_NAME_LANG', { 0: t('lang7') })}
          type="text"
          fullWidth
          name="categoryName07"
          value={editCategory.categoryName07}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.CATEGORY_NAME,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_CATEGORY_NAME_LANG', { 0: t('lang8') })}
          type="text"
          fullWidth
          name="categoryName08"
          value={editCategory.categoryName08}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.CATEGORY_NAME,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_CATEGORY_NAME_LANG', { 0: t('lang9') })}
          type="text"
          fullWidth
          name="categoryName09"
          value={editCategory.categoryName09}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.CATEGORY_NAME,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_CATEGORY_NAME_LANG', { 0: t('lang10') })}
          type="text"
          fullWidth
          name="categoryName10"
          value={editCategory.categoryName10}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.CATEGORY_NAME,
            },
          }}
        />
        {isAdmin && (
          <Box sx={{ ml: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editCategory.isOptionalAddition}
                  onChange={(e) => {
                    handleInputChange({
                      target: {
                        name: 'isOptionalAddition',
                        value: e ? e.target.checked : '',
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                />
              }
              label={t('LBL_MANAGE_INSPECTION_OPTIONAL_ADDITION')}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => closeModal(true)}
          color="inherit"
          variant="contained"
          sx={{ width: '120px', mr: 2, my: 1 }}
        >
          {t('BTN_CANCEL')}
        </Button>
        <Button
          onClick={onClickSave}
          color="primary"
          variant="contained"
          sx={{ width: '120px', mr: 3, my: 1 }}
          disabled={isDisabledSaveBtn}
        >
          {t('BTN_SAVE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCategoryModal;
